import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './components/AuthProvider';
import { purchaseCredits, confirmCreditPurchase, sendCAPIEvent } from './lib/api';
import { trackMetaEvent } from './lib/meta';
import { getAdAttribution } from './lib/adAttribution';
import './ReferralActivatePage.css';

const TIERS = [
  { cents: 1000, label: '$10', recommended: true },
  { cents: 2500, label: '$25', recommended: false },
  { cents: 5000, label: '$50', recommended: false },
];

export default function ReferralActivatePage() {
  const { user, loading } = useAuthContext();
  const navigate = useNavigate();
  const [selectedCents, setSelectedCents] = useState(1000);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const [credited, setCredited] = useState(false);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  // Check for Stripe checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const creditsStatus = params.get('credits');
    const sessionId = params.get('session_id');

    if (creditsStatus === 'success' && sessionId) {
      setCredited(true);
      // Confirm the purchase via fallback endpoint
      confirmCreditPurchase(sessionId).then((result) => {
        const purchaseValue = result?.creditedCents;
        if (purchaseValue) {
          trackMetaEvent('Purchase', {
            value: purchaseValue / 100,
            currency: 'USD',
            content_type: 'credits',
            order_id: sessionId,
            num_items: 1,
          }, `purchase_${sessionId}`);
        }
      }).catch(() => {});
      // Clean URL
      const url = new URL(window.location);
      url.searchParams.delete('credits');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.pathname);
    }
  }, []);

  async function handlePurchase() {
    setPurchasing(true);
    setError(null);
    try {
      const currentUrl = window.location.href.split('?')[0];
      const attribution = getAdAttribution();
      const eventId = trackMetaEvent('InitiateCheckout', {
        value: selectedCents / 100,
        currency: 'USD',
        content_type: 'credits',
        num_items: 1,
      });
      sendCAPIEvent('InitiateCheckout', eventId, {
        value: selectedCents / 100,
        currency: 'USD',
        num_items: 1,
      }, attribution).catch(() => {});
      const result = await purchaseCredits(
        selectedCents,
        `${currentUrl}?credits=success&session_id={CHECKOUT_SESSION_ID}`,
        `${currentUrl}?credits=cancelled`,
        { eventId, fbc: attribution.fbc, fbp: attribution.fbp },
      );
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      setError('Failed to start checkout');
      setPurchasing(false);
    }
  }

  if (loading) return null;

  if (credited) {
    return (
      <div className="referral-activate">
        <div className="referral-activate-card">
          <div className="referral-activate-success-icon">&#10003;</div>
          <h1 className="referral-activate-title">You're all set!</h1>
          <p className="referral-activate-subtitle">
            Your credits have been added. You and your friend will each receive
            <strong> $10 in bonus credits</strong>.
          </p>
          <button
            type="button"
            className="referral-activate-primary"
            onClick={() => navigate('/', { replace: true })}
          >
            Start creating
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-activate">
      <div className="referral-activate-card">
        <div className="referral-activate-badge">ALMOST THERE</div>

        <h1 className="referral-activate-title">Unlock your $10 bonus</h1>
        <p className="referral-activate-subtitle">
          Purchase $10 or more in credits to activate the referral bonus.
          Both you and your friend will get <strong>$10 free</strong>.
        </p>

        <div className="referral-activate-breakdown">
          <div className="referral-activate-breakdown-row">
            <span>Your purchase</span>
            <span className="referral-activate-amount">${(selectedCents / 100).toFixed(0)}</span>
          </div>
          <div className="referral-activate-breakdown-row referral-activate-bonus">
            <span>Referral bonus</span>
            <span className="referral-activate-amount">+$10</span>
          </div>
          <div className="referral-activate-breakdown-divider" />
          <div className="referral-activate-breakdown-row referral-activate-total">
            <span>Total credits</span>
            <span className="referral-activate-amount">
              ${((selectedCents / 100) + 10).toFixed(0)}
            </span>
          </div>
        </div>

        <div className="referral-activate-tiers">
          {TIERS.map((tier) => (
            <button
              key={tier.cents}
              type="button"
              className={`referral-activate-tier${selectedCents === tier.cents ? ' selected' : ''}`}
              onClick={() => { setSelectedCents(tier.cents); setError(null); }}
            >
              <span className="referral-activate-tier-amount">{tier.label}</span>
              {tier.recommended && (
                <span className="referral-activate-tier-tag">min</span>
              )}
            </button>
          ))}
        </div>

        {error && <p className="referral-activate-error">{error}</p>}

        <button
          type="button"
          className="referral-activate-primary"
          disabled={purchasing}
          onClick={handlePurchase}
        >
          {purchasing
            ? 'Redirecting to checkout...'
            : `Buy $${(selectedCents / 100).toFixed(0)} + get $10 free`}
        </button>

        <button
          type="button"
          className="referral-activate-skip"
          onClick={() => navigate('/', { replace: true })}
        >
          I'll do this later
        </button>
      </div>
    </div>
  );
}
